import * as React from "react";

import { isBrowserChromiumBased } from "@/shared/environment/userEnvironment";

import { PlotLoadingState } from "../messaging/events";

const LOADING_LONG_THRESHOLD_MS = 5000;
const LOADING_TOO_LONG_TRY_CHROMIUM_THRESHOLD_MS = 90 * 1000;

/**
 * Identify when data loads are taking a while to signal
 * that a loading indicator should be used.
 */
export function useShowBackgroundLoading(
  isPlotLoading: PlotLoadingState,
): boolean {
  const [showBackgroundLoading, setShowBackgroundLoading] =
    React.useState(false);
  const longLoadingTimeout = React.useRef<number>();
  React.useEffect(
    function showBackgroundLoadingIndicator() {
      // Clear any existing long loading timeout and loading indicator
      // when the loading state changes.
      clearTimeout(longLoadingTimeout.current);
      setShowBackgroundLoading(false);
      // Enable the background loading indicator after the background loading stage
      // takes at least the given duration.
      if (isPlotLoading === "background") {
        longLoadingTimeout.current = window.setTimeout(() => {
          setShowBackgroundLoading(true);
        }, LOADING_LONG_THRESHOLD_MS);
      }
      return function cleanUp() {
        clearTimeout(longLoadingTimeout.current);
      };
    },
    [isPlotLoading],
  );
  return isPlotLoading === "background" && showBackgroundLoading;
}

const LSKEY_SUGGEST_CHROMIUM_HIDDEN = "PlotPanel.SUGGEST_CHROMIUM_HIDDEN";
/**
 * We've found that Chromium gives better performance at loading large datasets.
 *
 * Until we can get to the bottom of why, advise users of other browsers what's up
 * if their data loads take a very long time.
 */
export function useSuggestChromiumIfLoadingTooLong(
  isPlotLoading: PlotLoadingState,
) {
  const [loadedTooLong, setLoadedTooLong] = React.useState(false);
  const [suggestionShown, setSuggestionShown] = React.useState(() => {
    // Dave: maybe be less pushy and switch to localStorage instead of sessionStorage.
    return sessionStorage.getItem(LSKEY_SUGGEST_CHROMIUM_HIDDEN) === "true";
  });
  const suggestChromiumLongLoadingTimeout = React.useRef<number>();
  const isChromium = isBrowserChromiumBased();

  function hideChromiumSuggestion() {
    setSuggestionShown(true);
    sessionStorage.setItem(LSKEY_SUGGEST_CHROMIUM_HIDDEN, "true");
  }

  React.useEffect(
    function showSuggestChromiumEffect() {
      clearTimeout(suggestChromiumLongLoadingTimeout.current);
      setLoadedTooLong(false);
      if (isPlotLoading === "background") {
        suggestChromiumLongLoadingTimeout.current = window.setTimeout(() => {
          setLoadedTooLong(true);
        }, LOADING_TOO_LONG_TRY_CHROMIUM_THRESHOLD_MS);
      }
      return function cleanUp() {
        clearTimeout(suggestChromiumLongLoadingTimeout.current);
      };
    },
    [isPlotLoading],
  );
  return {
    showChromiumSuggestion: !isChromium && loadedTooLong && !suggestionShown,
    hideChromiumSuggestion,
  };
}
